// WO-2928 / WO-2942: the per-app contacts boundary. Shared code never picks the
// contacts table or an owner itself; it asks this adapter. Which partner maps to
// which owner is configuration (see src/config/contactAppScopes.js), registered
// at app start, so this shared lib imports no app module and bakes in no app id.
//
// Until the database carries the app_scope JWT claim (WO-2942 claim-path files
// are applied by the operator) the adapter keeps filtering by an explicit owner,
// so nothing depends on the claim existing. Once row level security is on, the
// same filter simply agrees with the policy.
export const CONTACT_APP_SCOPES = Object.freeze({ CHARLIE: 'charlie', KCB: 'kcb', GSC: 'gsc' });
const DEFAULT_SCOPE = CONTACT_APP_SCOPES.CHARLIE;

// partner id -> owner value. Filled by configureContactScopes (app config), never hard-coded here.
let partnerScopes = {};
let childTables = { visits: '', photos: '' }; // set by configureContactScopes from app config
// The app the signed-in session is acting for right now (set by the app shell).
let activeScope = DEFAULT_SCOPE;

export function configureContactScopes(map = {}, tables) {
  partnerScopes = { ...map };
  if (tables) childTables = { visits: tables.visits || '', photos: tables.photos || '' };
}

export const contactOwnerForApp = (app) => {
  if (typeof app === 'string' && app) return app;
  if (app && app.id && partnerScopes[app.id]) return partnerScopes[app.id];
  return DEFAULT_SCOPE;
};

// The shell calls this whenever the active partner changes so helpers that have no
// component context (lib functions, widgets) read as the app that is open.
export function setActiveContactScope(app) {
  activeScope = contactOwnerForApp(app);
  return activeScope;
}
export const getActiveContactScope = () => activeScope;

// app === undefined means "the app that is open"; null or an unknown shell means the default (Charlie).
const scopeFor = (app) => (app === undefined ? activeScope : contactOwnerForApp(app));

// Shared read boundary: callers may add their usual filters after this point,
// but do not choose a table or forget the app owner themselves.
export function contactQuery(client, app, columns = '*', options) {
  const scope = scopeFor(app);
  const query = client.from(scope === CONTACT_APP_SCOPES.GSC ? 'oilfield_audience_contacts' : 'contacts');
  const selected = options === undefined ? query.select(columns) : query.select(columns, options);
  if (scope === CONTACT_APP_SCOPES.GSC) return selected;
  // A real PostgREST builder always has .eq; a bare stub that only resolves rows (older unit-test
  // doubles) has nothing to filter, so it is returned as is.
  return selected && typeof selected.eq === 'function' ? selected.eq('owner', scope) : selected;
}
export const readContacts = contactQuery;

// Visits and photos read boundary (PROVISIONAL: these tables hang off a contact's service job, so they
// carry no owner column of their own; the database app_scope policy on the parent contact is what scopes them,
// and the Grand Slam Caps view has no visits or photos at all).
export function readVisits(client, app, columns = '*') {
  if (scopeFor(app) === CONTACT_APP_SCOPES.GSC) throw new Error('The audience view has no visits');
  return client.from(childTables.visits).select(columns);
}
export function readPhotos(client, app, columns = '*') {
  if (scopeFor(app) === CONTACT_APP_SCOPES.GSC) throw new Error('The audience view has no photos');
  return client.from(childTables.photos).select(columns);
}

// For callers that build the query themselves (the app-wide paged loaders):
// applies the same owner scope to an already-selected contacts query.
export function withContactOwner(query, app) {
  const scope = contactOwnerForApp(app);
  return scope === CONTACT_APP_SCOPES.GSC ? query : query.eq('owner', scope);
}

// For by-id reads that must keep their exact query shape: a row that carries an
// owner is visible only to its own app (rows without an owner field pass).
export function contactVisibleToApp(row, app) {
  if (!row) return false;
  const scope = contactOwnerForApp(app);
  return scope === CONTACT_APP_SCOPES.GSC || !row.owner || row.owner === scope;
}

export function contactsForApp(client, app, columns = '*') {
  return contactQuery(client, app, columns);
}

const stamp = (scope, rows) => {
  const one = (r) => (r && typeof r === 'object' && !Array.isArray(r) && r.owner === undefined ? { ...r, owner: scope } : r);
  return Array.isArray(rows) ? rows.map(one) : one(rows);
};

// Write boundary. New rows are stamped with the open app's owner (unless the caller set one);
// updates and deletes pass through and stay keyed by the caller's id filters. The Grand Slam
// audience view is read-only, so a view-scoped session cannot write at all.
export function writeContacts(client, app) {
  const scope = scopeFor(app);
  if (scope === CONTACT_APP_SCOPES.GSC) {
    const denied = () => { throw new Error('The audience view is read-only'); };
    return { insert: denied, upsert: denied, update: denied, delete: denied };
  }
  const table = () => client.from('contacts');
  return {
    insert: (rows, ...rest) => table().insert(stamp(scope, rows), ...rest),
    upsert: (rows, ...rest) => table().upsert(stamp(scope, rows), ...rest),
    update: (...args) => table().update(...args),
    delete: (...args) => table().delete(...args),
  };
}
